/**
 * One reporting window for the whole Management screen, held in the URL.
 *
 * CORE-M-12 / Gap A. The window selector used to live inside
 * `AdsManagementTab`, so it only reached that component's own four queries.
 * Everything else picked its own 30 days: `AdReviewTab` (AD_REVIEW_DAYS),
 * `useManagementCounts`, and `CampaignDrilldown` (AD_HEALTH_WINDOW_DAYS).
 * Selecting "Last 90 days" therefore left Ad Review still printing
 * "$671 last 30 days" — a view ignoring its own selector, which is Art's
 * 2026-09-22 M-12 finding. The comment in `AdsManagementTab` claimed "ONE
 * WINDOW FOR THE WHOLE SCREEN"; it had stopped being true of the page.
 *
 * Why the URL rather than props or a context: the tab bodies are seven
 * sibling components plus a drill-down, and threading `days` through all of
 * them (and their query keys) is a wide, easy-to-miss change. `?tab=` is
 * already carried this way by `useManagementTabFromUrl`, and WhatConverts
 * mirrors `?days=`, so this follows a pattern operators and the codebase
 * already have. It also makes a window shareable: "look at this at 90 days"
 * becomes a link.
 *
 * Anything that only displays the window should use
 * `useManagementWindowDays()` and stay away from the setter, so only the one
 * visible `<Select>` can change it.
 */
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/** The windows an operator actually reasons in. */
export const MANAGEMENT_WINDOWS = [
  { days: 7, label: 'Last 7 days' },
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last 90 days' },
] as const;

export const DEFAULT_MANAGEMENT_WINDOW_DAYS = 30;

const ALLOWED = new Set<number>(MANAGEMENT_WINDOWS.map((w) => w.days));

/**
 * Parse `?days=`, falling back to the default.
 *
 * Anything not in the offered set is rejected rather than clamped: a
 * hand-typed `?days=45` silently answered as 30 would make the page disagree
 * with its own label, which is the class of bug this hook exists to remove.
 */
export function parseManagementWindow(raw: string | null): number {
  if (!raw) return DEFAULT_MANAGEMENT_WINDOW_DAYS;
  const n = Number(raw);
  if (!Number.isFinite(n)) return DEFAULT_MANAGEMENT_WINDOW_DAYS;
  return ALLOWED.has(n) ? n : DEFAULT_MANAGEMENT_WINDOW_DAYS;
}

/** The selected window. Every card and table on Management reads this. */
export function useManagementWindowDays(): number {
  const [params] = useSearchParams();
  return parseManagementWindow(params.get('days'));
}

/** The selected window plus its setter, for the one visible picker. */
export function useManagementWindow(): [number, (days: number) => void] {
  const [params, setParams] = useSearchParams();
  const days = parseManagementWindow(params.get('days'));

  const setDays = useCallback(
    (next: number) => {
      const p = new URLSearchParams(params);
      if (next === DEFAULT_MANAGEMENT_WINDOW_DAYS) {
        // Keep the default out of the URL so a shared link is the short form.
        p.delete('days');
      } else {
        p.set('days', String(next));
      }
      setParams(p, { replace: false });
    },
    [params, setParams],
  );

  return [days, setDays];
}
